// The panel's own map reads React's synthetic key event; the document listener
// below reads the DOM's. Both are called `KeyboardEvent`, so React's is renamed
// here rather than left to shadow the global one it is not.
import { useEffect, useId, useRef, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

import { useFormFactor } from '@/shared/workspace/formFactor'

import { useBackEntry } from './backStack'
import { focusableControls } from './focusableControls'

export type ModalDialogRole = 'dialog' | 'alertdialog'
export type ModalDialogTone = 'neutral' | 'warning' | 'danger'

export interface ModalDialogProps {
  role?: ModalDialogRole
  tone?: ModalDialogTone
  /**
   * 'wide' is for content that is a table or a list registry, not a form.
   * 'workbench' is for content that is TWO panes read against each other — a
   * document beside the values taken out of it — which needs the screen rather
   * than a reading column.
   */
  size?: 'regular' | 'wide' | 'workbench'
  /**
   * THE PANEL AS A RULED FRAME: the title, the body and the action row each
   * run edge to edge with a hairline between them, and the body takes the
   * height the other two leave. The padded panel scrolls as one sheet, so it
   * cannot hold a pane that stays in view while its neighbour scrolls; a
   * framed body hands its whole box to the caller, who decides what scrolls.
   */
  framed?: boolean
  title: ReactNode
  description?: ReactNode
  children?: ReactNode
  actions?: ReactNode
  /**
   * WHAT THE ACTION ROW SAYS BEFORE ITS ACTS: the one sentence that tells the
   * reader what the confirming act will do, or why it is refused. It takes the
   * room the buttons leave, so they keep the row's end; where the row is too
   * narrow for both, the sentence takes the line above them. Drawn only with
   * `actions` — a sentence about acts that are not there says nothing.
   */
  actionsNote?: ReactNode
  onDismiss?: () => void
  /** A distinct confirmation action invoked by Enter outside multiline controls. */
  onConfirm?: () => void
  dismissOnBackdrop?: boolean
}

/**
 * THE INNERMOST DIALOG ON THE SCREEN, read off the document rather than kept in
 * a register of its own: every dialog is portaled to `<body>` in the order it
 * was raised, so the last `.modal-dialog` in the document is the one standing
 * over the others.
 */
function innermostDialog(): Element | null {
  const standing = document.querySelectorAll('.modal-dialog')
  return standing[standing.length - 1] ?? null
}

export function ModalDialog({
  role = 'dialog',
  tone = 'neutral',
  size = 'regular',
  framed = false,
  title,
  description,
  children,
  actions,
  actionsNote,
  onDismiss,
  onConfirm,
  dismissOnBackdrop = false,
}: ModalDialogProps) {
  const titleId = useId()
  const descriptionId = useId()
  const rootRef = useRef<HTMLDivElement | null>(null)
  const panelRef = useRef<HTMLDivElement | null>(null)
  const previousFocusRef = useRef<HTMLElement | null>(null)
  /* Read through a ref so the listener below is attached once per opening,
     while the caller keeps passing a fresh closure on every render. */
  const dismissRef = useRef(onDismiss)
  dismissRef.current = onDismiss
  const dismissable = onDismiss !== undefined
  const hasBody = description !== undefined || children !== undefined
  /*
   * ON A PHONE THE DIALOG RISES FROM THE BOTTOM EDGE, and its exits are the
   * platform's: the Back gesture, a tap on the scrim, Escape from a keyboard,
   * and the action row. There is no X — there never was one on the desktop
   * either; a dialog's exit is `onDismiss`, and a dialog that must not be
   * left without a decision passes none and keeps every one of these shut.
   * The scrim dismisses here whatever `dismissOnBackdrop` says: on a desktop
   * a click beside a form is often a slip, on a phone a tap on the veil is
   * the one gesture every sheet on the device answers.
   */
  const handheld = useFormFactor() === 'handheld'
  useBackEntry(handheld && onDismiss !== undefined, () => onDismiss?.())

  useEffect(() => {
    previousFocusRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null
    panelRef.current?.focus()

    return () => {
      previousFocusRef.current?.focus()
    }
  }, [])

  /*
   * ESCAPE HAS A SECOND EAR, AND IT IS THE DOCUMENT. The panel's own key map
   * below hears the key only while focus stands inside the dialog, and focus
   * leaves on the most ordinary gesture there is — a press on the veil beside
   * the panel, which on a desktop dialog does nothing else — or when the
   * control holding it goes disabled under a pending write. From that moment
   * the dialog stood with Escape dead and the buttons as its only way out,
   * which is not what a reader who has just pressed beside a dialog expects of
   * the key. So the document is listened to for the whole time the dialog
   * stands, and only for a press the dialog's own tree did not already take:
   * a key struck inside the panel, or inside a popover portaled out of it, is
   * stopped where it is handled and never reaches here.
   *
   * ONLY THE INNERMOST DIALOG ANSWERS. Escape takes down the surface raised
   * last, and a dialog that passes no `onDismiss` is a question that must be
   * answered: standing on top it listens for nothing, and the dialog beneath it
   * stays shut rather than being dismissed through it.
   */
  useEffect(() => {
    if (!dismissable) {
      return undefined
    }
    const onEscape = (event: KeyboardEvent) => {
      const root = rootRef.current
      if (event.key !== 'Escape' || root === null || innermostDialog() !== root) {
        return
      }
      if (event.target instanceof Node && root.contains(event.target)) {
        return
      }
      event.preventDefault()
      dismissRef.current?.()
    }
    document.addEventListener('keydown', onEscape)
    return () => document.removeEventListener('keydown', onEscape)
  }, [dismissable])

  function onKeyDown(event: ReactKeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Escape' && onDismiss) {
      event.preventDefault()
      event.stopPropagation()
      onDismiss()
      return
    }
    if (
      event.key === 'Enter' &&
      onConfirm &&
      !event.altKey &&
      !event.ctrlKey &&
      !event.metaKey &&
      !event.shiftKey &&
      !event.repeat &&
      !event.nativeEvent.isComposing &&
      !handlesEnterNatively(event.target)
    ) {
      event.preventDefault()
      event.stopPropagation()
      onConfirm()
      return
    }
    if (event.key !== 'Tab') {
      return
    }

    const panel = panelRef.current
    if (!panel) {
      return
    }
    const controls = focusableControls(panel)
    if (controls.length === 0) {
      event.preventDefault()
      panel.focus()
      return
    }

    const first = controls[0]!
    const last = controls[controls.length - 1]!
    if (event.shiftKey && (document.activeElement === first || document.activeElement === panel)) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  return createPortal(
    <div
      className={
        handheld
          ? `modal-dialog modal-dialog--${tone} modal-dialog--handheld`
          : `modal-dialog modal-dialog--${tone}`
      }
      onKeyDown={onKeyDown}
      ref={rootRef}
    >
      <div
        className="modal-dialog__scrim"
        aria-hidden="true"
        /* The press is refused its default so it cannot carry focus out of the
           dialog: a modal whose focus has gone to `body` answers neither Tab
           nor its own key map. Whether the press also DISMISSES is the
           question above; taking focus away was never part of the answer. */
        onMouseDown={(event) => {
          event.preventDefault()
          if (dismissOnBackdrop || handheld) {
            onDismiss?.()
          }
        }}
      />
      <div
        className={[
          'modal-dialog__panel',
          size === 'regular' ? '' : `modal-dialog__panel--${size}`,
          framed ? 'modal-dialog__panel--framed' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        role={role}
        aria-modal="true"
        aria-labelledby={titleId}
        /* The DESCRIPTION div only, never the whole body: a described-by that
           covers the content makes a form dialog read every control's text as
           its "description" before the first field is reached. */
        aria-describedby={description !== undefined ? descriptionId : undefined}
        tabIndex={-1}
        ref={panelRef}
      >
        <h2 className="modal-dialog__title" id={titleId}>
          {title}
        </h2>
        {hasBody ? (
          <div className="modal-dialog__body">
            {description !== undefined ? (
              <div className="modal-dialog__description" id={descriptionId}>{description}</div>
            ) : null}
            {children !== undefined ? (
              <div className="modal-dialog__content">{children}</div>
            ) : null}
          </div>
        ) : null}
        {actions !== undefined ? (
          <div
            className={
              actionsNote !== undefined
                ? 'modal-dialog__actions modal-dialog__actions--noted'
                : 'modal-dialog__actions'
            }
          >
            {actionsNote !== undefined ? (
              <div className="modal-dialog__note">{actionsNote}</div>
            ) : null}
            {actions}
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  )
}

function handlesEnterNatively(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLElement &&
    // `[role="combobox"]` is the searchable list's own input: Enter there
    // chooses a row, or deliberately chooses nothing while several rows stand.
    // Neither press is this dialog's confirmation.
    (target.matches('button, a[href], textarea, select, [role="combobox"]') ||
      target.isContentEditable)
  )
}
